import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { Box, Drawer, IconButton, Skeleton, Stack, Typography } from "@mui/material";
import { InfoIconTooltip } from "components/InfoIconTooltip";
import { TagsDisplay } from "components/TagsDisplay";
import { FacilityAlternateIdKnownKind } from "encamp-shared/src/facility/alternateId";
import { findAlternateId } from "encamp-shared/src/facilityAlternateId/findAlternateId";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { formatAddress } from "encamp-shared/src/utils/address";
import { FacilityWasteView } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { Suspense, useMemo, useState } from "react";
import { useParams } from "react-router";
import { buildWasteStreamDiscrepanciesPath } from "routes/Customer/Waste/ManifestLineItems/wasteStreamDiscrepanciesLink";

import { DiscrepanciesSection } from "../../../Manifests/sections/DiscrepanciesSection";
import { SectionCard } from "../../../Manifests/sections/SectionCard";
import { SectionHeader } from "../../../Manifests/sections/SectionHeader";
import { formatShipmentRangeDate } from "../formatShipmentRangeDate";
import { MergedWasteStreamChip } from "../MergedWasteStreamChip";
import { ProvisionalWasteStreamChip } from "../ProvisionalWasteStreamChip";
import { useWasteFacility } from "../useWasteFacility";
import { useWasteFacilityState } from "../WasteFacilityContext";
import { WasteStreamUnmergeDialog } from "../WasteStreamsDataGrid/WasteStreamMerge/WasteStreamUnmergeDialog";
import { useWasteStream } from "./api";
import { DestinationsDataGrid } from "./Destinations/DestinationsDataGrid";
import { EditWasteStreamDialog } from "./EditWasteStreamDialog";
import { ManifestLineItemsDataGrid } from "./ManifestLineItemsDataGrid";
import { WasteProfileSections } from "./Profile/WasteProfileSections";
import { WasteStreamCompareDialog } from "./WasteStreamCompareDialog";
import { WasteStreamInfoSection } from "./WasteStreamInfoSection";
import { WasteStreamShipmentsByMonth } from "./WasteStreamShipmentsByMonth";

interface WasteStreamDetailsModalProps {
  open: boolean;
  onClose: () => void;
  wasteStreamId: string;
  /**
   * Builds the href a manifest-line-item row in the embedded grid points at.
   * Used for `getRowLink` (middle-click, open in new tab) and as the
   * fallback target for plain left-clicks when no `onLineItemClick` override
   * is provided.
   */
  buildLineItemHref: (lineItemId: string) => string;
  /**
   * Optional override for plain left-clicks on a line-item row. Defaults to
   * navigating to `buildLineItemHref(id)`. The tenant Waste Streams page
   * passes an override that appends the line id to the URL so the line-item
   * drawer opens on top of this modal instead of replacing it.
   */
  onLineItemClick?: (lineItemId: string) => void;
}

const getReadonlyWasteViewText = (view: FacilityWasteView) => {
  switch (view) {
    case FacilityWasteView.All:
      return "Displaying all waste";
    case FacilityWasteView.Reportable: {
      return "Displaying only reportable waste";
    }
    case FacilityWasteView.NotReportable: {
      return "Displaying only non-reportable waste";
    }
  }
};

// Single loading skeleton for the entire modal
const WasteStreamDetailsModalSkeleton = () => (
  <Box sx={{ p: 3 }}>
    <Stack gap={3}>
      {/* Header skeleton */}
      <Box>
        <Skeleton variant="text" width={300} height={40} />
        <Skeleton variant="text" width={200} height={20} sx={{ mt: 1 }} />
      </Box>

      {/* Content skeleton */}
      <Skeleton variant="rectangular" height={200} width="100%" />
      <Skeleton variant="rectangular" height={200} width="100%" />
      <Skeleton variant="rectangular" height={50} width="100%" />
      <Skeleton variant="rectangular" height={150} width="100%" />
      <Skeleton variant="rectangular" height={150} width="100%" />
    </Stack>
  </Box>
);

// Main content component that gets wrapped in Suspense
const WasteStreamDetailsContent = ({
  wasteStreamId,
  onClose,
  buildLineItemHref,
  onLineItemClick,
}: {
  wasteStreamId: string;
  onClose: () => void;
  buildLineItemHref: (lineItemId: string) => string;
  onLineItemClick?: (lineItemId: string) => void;
}) => {
  const {
    wasteFacilityState: { deferredFilters },
    effectiveWasteFacilityId,
  } = useWasteFacilityState();
  const { data: facilityData } = useWasteFacility(effectiveWasteFacilityId);
  const { data: wasteStreamData } = useWasteStream(wasteStreamId);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [unmergeDialogOpen, setUnmergeDialogOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);

  const { isStaff } = useCurrentUser();

  const { featureFlags } = useFeatureFlags();
  const containerTrackingEnabled = !!featureFlags[FeatureFlagKey.ContainerTracking];

  // "Review" opens this stream's discrepant line items in a new tab — the
  // line-item list filters by waste stream + unresolved discrepancy count.
  const { tenantId } = useParams<{ tenantId: string }>();
  const reviewHref = useMemo(
    () => buildWasteStreamDiscrepanciesPath({ tenantId: tenantId ?? "", wasteStreamId }),
    [tenantId, wasteStreamId]
  );

  const facility = facilityData?.facility;

  // Get facility alternate IDs
  const epaId = findAlternateId(facility?.facilityAlternateIds ?? [], [
    FacilityAlternateIdKnownKind.EPA,
  ])?.value;

  const { startDate, endDate, view, tags } = deferredFilters;
  const formattedDateRange = useMemo(() => {
    return formatShipmentRangeDate({
      startDate,
      endDate,
    });
  }, [startDate, endDate]);

  // Check if waste stream is merged
  const isMerged = (wasteStreamData?.wasteStream?.mergeCount ?? 0) > 1;

  const handleEditClick = () => {
    if (isMerged) {
      setUnmergeDialogOpen(true);
    } else {
      setEditDialogOpen(true);
    }
  };

  return (
    <>
      {/* White Content Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          backgroundColor: "background.paper",
          position: "sticky",
          top: 0,
          zIndex: 2,
        }}
      >
        {/* Title Row with Edit Icon and Action Buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <WasteStreamDetailTitle wasteStreamId={wasteStreamId} onEditClick={handleEditClick} />
          {/* Date range / view + close (top line, right-aligned) */}
          <Stack direction="row" spacing={1} alignItems="flex-start" flexShrink={0} sx={{ pl: 5 }}>
            <Stack spacing={0.5} alignItems="flex-end">
              <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-word" }}>
                {formattedDateRange}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {getReadonlyWasteViewText(view ?? FacilityWasteView.All)}
              </Typography>
              {tags && tags.length > 0 && (
                <TagsDisplay tags={tags?.map((tag) => ({ id: tag, name: tag })) ?? []} />
              )}
            </Stack>
            <IconButton onClick={onClose} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>

        {/* Facility Details */}
        <Typography variant="caption" color="text.secondary">
          {facility?.name}{" "}
          {facility?.customerFacilityId ? `(${facility?.customerFacilityId}) ` : ""}- EPA ID:{" "}
          {epaId} -{" "}
          {formatAddress(facility?.streetAddress1, facility?.city, facility?.state, facility?.zip)}
        </Typography>
      </Box>

      {/* Content — gray section cards on white, matching the Manifest drawer. */}
      <Stack sx={{ height: "100%" }} gap={3} px={3} py={2}>
        {/* Two-column: details (left, narrow/tall) + shipments (right). CSS grid
            so both columns stretch to the same (tallest) row height — a grid cell
            gives the right column a definite height to bottom-align its last card
            against, which flex `align-items: stretch` does not. Collapses to a
            single column on small screens. */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "5fr 7fr" },
            gap: 3,
            alignItems: "stretch",
          }}
        >
          <SectionCard>
            <SectionHeader title="Details">
              <InfoIconTooltip tooltip="Manifest line items are grouped into one waste stream when they share the same generator, profile or shipping description, federal and state waste codes, form code, source code, and waste classification. The pinned field below shows the profile or shipping description value that identified this stream." />
            </SectionHeader>
            <WasteStreamInfoSection
              wasteStreamId={wasteStreamId}
              facilityState={facility?.state || undefined}
            />
          </SectionCard>
          {/* The grid cell stretches to the row height. The chart card grows to
              fill it (flexGrow) so there's no dead space between it and the
              bottom-aligned discrepancies card — and so it still fills the column
              when there's no discrepancies card (non-staff). `useFlexGap` makes
              spacing use CSS gap instead of injected child margins so the
              flex-grow math isn't thrown off. */}
          <Stack spacing={3} useFlexGap sx={{ minWidth: 0 }}>
            <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
              <SectionCard>
                {/* The chart owns its own empty state (rendered from the same
                    query that draws the bars) so the "No shipment data" message
                    can't disagree with what the chart would show. */}
                <WasteStreamShipmentsByMonth
                  wasteStreamId={wasteStreamId}
                  startDate={startDate ?? ""}
                  endDate={endDate ?? ""}
                  effectiveAmount={wasteStreamData?.wasteStream?.effectiveAmount ?? 0}
                  view={view ?? FacilityWasteView.All}
                  tags={tags ?? []}
                />
              </SectionCard>
            </Box>
            {isStaff && (
              <Box>
                <SectionCard>
                  <DiscrepanciesSection
                    discrepancies={wasteStreamData?.wasteStream?.discrepancies ?? []}
                    onOpenDiscrepancies={() =>
                      window.open(reviewHref, "_blank", "noopener,noreferrer")
                    }
                    canReview
                    emptyMessage="No discrepancies on this waste stream"
                  />
                </SectionCard>
              </Box>
            )}
          </Stack>
        </Box>

        {/*
          Project 2's customer-maintained profile: characterization, the
          profile sheet, constituents, and the re-certification schedule. Each
          renders as its own section card. Gated on the Container Tracking
          flag — an un-flagged tenant never mounts these, so the drawer and
          its queries are unchanged for them.
        */}
        {containerTrackingEnabled && <WasteProfileSections wasteStreamId={wasteStreamId} />}

        <DestinationsDataGrid wasteStreamId={wasteStreamId} />

        <SectionCard>
          <ManifestLineItemsDataGrid
            wasteStreamId={wasteStreamId}
            buildLineItemHref={buildLineItemHref}
            onLineItemClick={onLineItemClick}
            discrepancies={wasteStreamData?.wasteStream?.discrepancies ?? []}
            onCompareClick={() => setCompareOpen(true)}
          />
        </SectionCard>
      </Stack>

      {/* Compare Data Sources */}
      {compareOpen && (
        <WasteStreamCompareDialog
          open={compareOpen}
          onClose={() => setCompareOpen(false)}
          wasteStreamId={wasteStreamId}
          streamName={wasteStreamData?.wasteStream?.name ?? "Waste Stream"}
        />
      )}

      {/* Edit Dialog */}
      {editDialogOpen && (
        <EditWasteStreamDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          wasteStreamId={wasteStreamId}
        />
      )}

      {/* Unmerge Dialog for merged waste streams */}
      {unmergeDialogOpen && wasteStreamData?.wasteStream && (
        <WasteStreamUnmergeDialog
          onClose={() => setUnmergeDialogOpen(false)}
          wasteStream={wasteStreamData.wasteStream}
        />
      )}
    </>
  );
};

export const WasteStreamDetailsModal = ({
  open,
  onClose,
  wasteStreamId,
  buildLineItemHref,
  onLineItemClick,
}: WasteStreamDetailsModalProps) => {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: 1350,
          maxWidth: "80%",
        },
      }}
    >
      {open && (
        <Suspense fallback={<WasteStreamDetailsModalSkeleton />}>
          <WasteStreamDetailsContent
            wasteStreamId={wasteStreamId}
            onClose={onClose}
            buildLineItemHref={buildLineItemHref}
            onLineItemClick={onLineItemClick}
          />
        </Suspense>
      )}
    </Drawer>
  );
};

const WasteStreamDetailTitle = ({
  wasteStreamId,
  onEditClick,
}: {
  wasteStreamId: string;
  onEditClick: () => void;
}) => {
  const { data: wasteStreamData } = useWasteStream(wasteStreamId);
  const { isStaff } = useCurrentUser();
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
      <Typography variant="h5" sx={{ wordBreak: "break-word", m: 0, p: 0 }}>
        {wasteStreamData?.wasteStream?.name || "Waste Stream"}
      </Typography>
      {isStaff && (
        <MergedWasteStreamChip
          mergeCount={wasteStreamData?.wasteStream?.mergeCount ?? 0}
          sx={{ mt: 0.5 }}
        />
      )}
      <ProvisionalWasteStreamChip
        isProvisional={wasteStreamData?.wasteStream?.isProvisional ?? false}
        sx={{ mt: 0.5 }}
      />

      {/* Edit Button for Staff */}
      {isStaff && (
        <IconButton
          size="small"
          onClick={onEditClick}
          sx={{
            color: "warning.main",
            flexShrink: 0,
            alignSelf: "flex-start",
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};
